/* eslint-disable react/require-optimization */

import {Component, type ReactNode} from 'react';
import {FormattedMessage} from 'react-intl';

import isTrue from '@tinkoff/utils/isTrue';

import {emitUserLoggedOutEvent} from 'actions/global_actions';
import Constants from 'utils/constants';
import * as Utils from 'utils/utils';
import {t} from 'utils/i18n';
import SettingItemMax from 'components/setting_item_max';
import SettingItemMin from 'components/setting_item_min';
import ConfirmModal from 'components/confirm_modal';
import BackIcon from 'components/widgets/icons/fa_back_icon';

import {type User} from 'features/users';

import {type PreferenceType} from '@mattermost/types/preferences';

import Preferences from 'mattermost-redux/constants/preferences';

import JoinLeaveSection from './join_leave_section';
import PerformanceDebuggingSection from './performance_debugging_section';

const {PRE_RELEASE_FEATURES} = Constants;

const PRE_RELEASE_FEATURES_KEYS = Object.keys(PRE_RELEASE_FEATURES) as Array<keyof typeof PRE_RELEASE_FEATURES>;
const PRE_RELEASE_FEATURES_KEYS_PREFIXED = Object.values(PRE_RELEASE_FEATURES).map(
    (feature) => `${Constants.FeatureTogglePrefix}${feature.label}`,
);

function getFeatureIntlId(feature: keyof typeof PRE_RELEASE_FEATURES) {
    return PRE_RELEASE_FEATURES[feature].intlId;
}

function getFeatureDescription(feature: keyof typeof PRE_RELEASE_FEATURES) {
    return PRE_RELEASE_FEATURES[feature].description;
}

type Props = {
    currentUser: User;
    advancedSettingsCategory: PreferenceType[];
    sendOnCtrlEnter: string;
    codeBlockOnCtrlEnter: string;
    formatting: string;
    joinLeave: string;
    updateSection?: (section: string) => void;
    activeSection?: string;
    closeModal: (...args: any[]) => void;
    collapseModal: (...args: any[]) => void;
    enablePreviewFeatures?: boolean;
    enableUserDeactivation?: boolean;
    actions: {
        savePreferences: (...args: any[]) => Promise<{data: true}>;
        updateUserActive: (...args: any[]) => Promise<{error?: Error; data?: true}>;
        revokeAllSessionsForUser: (...args: any[]) => Promise<{error?: Error; data?: true}>;
    };
};

type PreReleaseFeatureFlag = `${typeof Constants.FeatureTogglePrefix}${Lowercase<keyof typeof PRE_RELEASE_FEATURES>}`;

type State = {
    settings: {
        send_on_ctrl_enter: Props['sendOnCtrlEnter'];
        code_block_ctrl_enter: Props['codeBlockOnCtrlEnter'];
        formatting: Props['formatting'];
        join_leave: Props['joinLeave'];
        copy_login_with_at: string;
    } & Record<PreReleaseFeatureFlag, PreferenceType['value']>;
    enabledFeatures: number;
    isSaving: boolean;
    previewFeaturesEnabled: boolean;
    showDeactivateAccountModal: boolean;
    serverError?: string;
};

export default class AdvancedSettingsDisplay extends Component<Props, State> {
    state = {
        enabledFeatures: 0,
        isSaving: false,
        previewFeaturesEnabled: false,
        settings: {},
        showDeactivateAccountModal: false,
        serverError: '',
    } as State;

    private setInitialState() {
        const advancedSettings = this.props.advancedSettingsCategory;
        const settings = {
            send_on_ctrl_enter: this.props.sendOnCtrlEnter,
            code_block_ctrl_enter: this.props.codeBlockOnCtrlEnter,
            formatting: this.props.formatting,
            join_leave: this.props.joinLeave,
            copy_login_with_at: advancedSettings.find(({name}) => name === 'copy_login_with_at')?.value ?? 'true',
        } as State['settings'];

        let enabledFeatures = 0;

        const advancedSettingsFiltered = advancedSettings.filter(({name}) => {
            return PRE_RELEASE_FEATURES_KEYS_PREFIXED.includes(name);
        });

        for (const setting of advancedSettingsFiltered) {
            settings[setting.name as PreReleaseFeatureFlag] = setting.value;

            if (setting.value === 'true') {
                enabledFeatures += 1;
            }
        }

        const previewFeaturesEnabled = Boolean(this.props.enablePreviewFeatures);

        this.setState({
            settings,
            enabledFeatures,
            isSaving: false,
            previewFeaturesEnabled,
            showDeactivateAccountModal: false,
        });
    }

    componentDidMount(): void {
        this.setInitialState();
    }

    updateSetting = (settings: Partial<State['settings']>) => {
        this.setState((prevState) => ({
            settings: {
                ...prevState.settings,
                ...settings,
            },
        }));
    };

    toggleFeature = (feature: PreferenceType['name'], checked: boolean) => {
        const settings = this.state.settings;
        settings[(Constants.FeatureTogglePrefix + feature) as PreReleaseFeatureFlag] = String(checked);

        let enabledFeatures = 0;
        Object.keys(this.state.settings).forEach((setting) => {
            const settingValue = this.state.settings[setting as keyof State['settings']];
            if (setting.lastIndexOf(Constants.FeatureTogglePrefix) === 0 && settingValue === 'true') {
                enabledFeatures++;
            }
        });

        this.setState({settings, enabledFeatures});
    };

    saveEnabledFeatures = () => {
        const features: Array<keyof State['settings']> = [];
        Object.keys(this.state.settings).forEach((setting) => {
            if (setting.lastIndexOf(Constants.FeatureTogglePrefix) === 0) {
                features.push(setting as keyof State['settings']);
            }
        });

        this.handleSubmit(features);
    };

    handleSubmit = async (settings: Array<keyof State['settings']>) => {
        const preferences: PreferenceType[] = [];

        const {actions, currentUser} = this.props;
        const userId = currentUser.id;

        // this should be refactored so we can actually be certain about what type everything is
        (Array.isArray(settings) ? settings : [settings]).forEach((setting) => {
            preferences.push({
                user_id: userId,
                category: Constants.Preferences.CATEGORY_ADVANCED_SETTINGS,
                name: setting,
                value: this.state.settings[setting],
            });
        });

        this.setState({isSaving: true});
        await actions.savePreferences(userId, preferences);

        this.handleUpdateSection('');
    };

    handleDeactivateAccountSubmit = async () => {
        const userId = this.props.currentUser.id;

        this.setState({isSaving: true});

        this.props.actions.updateUserActive(userId, false).then(({error}) => {
            if (error) {
                this.setState({serverError: error.message});
            }
        });

        const {data, error} = await this.props.actions.revokeAllSessionsForUser(userId);
        if (data) {
            emitUserLoggedOutEvent();
        } else if (error) {
            this.setState({serverError: error.message});
        }
    };

    handleShowDeactivateAccountModal = () => {
        this.setState({
            showDeactivateAccountModal: true,
        });
    };

    handleHideDeactivateAccountModal = () => {
        this.setState({
            showDeactivateAccountModal: false,
        });
    };

    handleUpdateSection = (section: string) => {
        if (!section) {
            this.setInitialState();
        }
        this.setState({isSaving: false});
        this.props.updateSection?.(section);
    };

    // This function changes ctrl to cmd when OS is mac
    getCtrlSendText = () => {
        const description = {
            default: {
                id: t('user.settings.advance.sendDesc'),
                defaultMessage: 'When enabled, CTRL + ENTER will send the message and ENTER inserts a new line.',
            },
            mac: {
                id: t('user.settings.advance.sendDesc.mac'),
                defaultMessage: 'When enabled, ⌘ + ENTER will send the message and ENTER inserts a new line.',
            },
        };
        const title = {
            default: {
                id: t('user.settings.advance.sendTitle'),
                defaultMessage: 'Send Messages on CTRL+ENTER',
            },
            mac: {
                id: t('user.settings.advance.sendTitle.mac'),
                defaultMessage: 'Send Messages on ⌘+ENTER',
            },
        };
        if (Utils.isMac()) {
            return {
                ctrlSendTitle: title.mac,
                ctrlSendDesc: description.mac,
            };
        }
        return {
            ctrlSendTitle: title.default,
            ctrlSendDesc: description.default,
        };
    };

    renderOnOffLabel(enabled: string) {
        if (enabled === 'false') {
            return (
                <FormattedMessage
                    id='user.settings.advance.off'
                    defaultMessage='Off'
                />
            );
        }

        return (
            <FormattedMessage
                id='user.settings.advance.on'
                defaultMessage='On'
            />
        );
    }

    renderCtrlEnterLabel() {
        const ctrlEnter = this.state.settings.send_on_ctrl_enter;
        const codeBlockCtrlEnter = this.state.settings.code_block_ctrl_enter;
        if (ctrlEnter === 'false' && codeBlockCtrlEnter === 'false') {
            return (
                <FormattedMessage
                    id='user.settings.advance.off'
                    defaultMessage='Off'
                />
            );
        } else if (ctrlEnter === 'true' && codeBlockCtrlEnter === 'true') {
            return (
                <FormattedMessage
                    id='user.settings.advance.onForAllMessages'
                    defaultMessage='On for all messages'
                />
            );
        }
        return (
            <FormattedMessage
                id='user.settings.advance.onForCode'
                defaultMessage='On only for code blocks starting with ```'
            />
        );
    }

    renderFormattingSection = () => {
        if (this.props.activeSection === 'formatting') {
            return (
                <SettingItemMax
                    title={
                        <FormattedMessage
                            id='user.settings.advance.formattingTitle'
                            defaultMessage='Enable Post Formatting'
                        />
                    }
                    inputs={[
                        <fieldset key='formattingSetting'>
                            <legend className='form-legend hidden-label'>
                                <FormattedMessage
                                    id='user.settings.advance.formattingTitle'
                                    defaultMessage='Enable Post Formatting'
                                />
                            </legend>
                            <div className='radio'>
                                <label>
                                    <input
                                        id='postFormattingOn'
                                        type='radio'
                                        name='formatting'
                                        checked={this.state.settings.formatting !== 'false'}
                                        onChange={() => this.updateSetting({formatting: 'true'})}
                                    />
                                    <FormattedMessage
                                        id='user.settings.advance.on'
                                        defaultMessage='On'
                                    />
                                </label>
                                <br />
                            </div>
                            <div className='radio'>
                                <label>
                                    <input
                                        id='postFormattingOff'
                                        type='radio'
                                        name='formatting'
                                        checked={this.state.settings.formatting === 'false'}
                                        onChange={() => this.updateSetting({formatting: 'false'})}
                                    />
                                    <FormattedMessage
                                        id='user.settings.advance.off'
                                        defaultMessage='Off'
                                    />
                                </label>
                                <br />
                            </div>
                            <div className='mt-5'>
                                <FormattedMessage
                                    id='user.settings.advance.formattingDesc'
                                    defaultMessage='If enabled, posts will be formatted to create links, show emoji, style the text, and add line breaks. By default, this setting is enabled.'
                                />
                            </div>
                        </fieldset>,
                    ]}
                    setting={'formatting'}
                    submit={this.handleSubmit}
                    saving={this.state.isSaving}
                    serverError={this.state.serverError}
                    updateSection={this.handleUpdateSection}
                />
            );
        }

        return (
            <SettingItemMin
                title={
                    <FormattedMessage
                        id='user.settings.advance.formattingTitle'
                        defaultMessage='Enable Post Formatting'
                    />
                }
                describe={this.renderOnOffLabel(this.state.settings.formatting)}
                section={'formatting'}
                updateSection={this.handleUpdateSection}
            />
        );
    };

    renderCopyLoginWithAtSection = () => {
        if (this.props.activeSection === Preferences.ADVANCED_COPY_LOGIN_WITH_AT) {
            return (
                <SettingItemMax
                    title={
                        <FormattedMessage
                            id='user.settings.advance.copyLoginWithAt.title'
                            defaultMessage="Copy user's login with @ from profile"
                        />
                    }
                    inputs={[
                        <fieldset key='formattingSetting'>
                            <legend className='form-legend hidden-label'>
                                <FormattedMessage
                                    id='user.settings.advance.copyLoginWithAt.title'
                                    defaultMessage="Copy user's login with @ from profile"
                                />
                            </legend>
                            <div className='radio'>
                                <label>
                                    <input
                                        id='copyLoginWithAtOn'
                                        type='radio'
                                        name='copyLoginWithAt'
                                        checked={isTrue(this.state.settings.copy_login_with_at)}
                                        onChange={() => this.updateSetting({copy_login_with_at: 'true'})}
                                    />
                                    <FormattedMessage
                                        id='user.settings.advance.on'
                                        defaultMessage='On'
                                    />
                                </label>
                                <br />
                            </div>
                            <div className='radio'>
                                <label>
                                    <input
                                        id='copyLoginWithAtOff'
                                        type='radio'
                                        name='copyLoginWithAt'
                                        checked={!isTrue(this.state.settings.copy_login_with_at)}
                                        onChange={() => this.updateSetting({copy_login_with_at: 'false'})}
                                    />
                                    <FormattedMessage
                                        id='user.settings.advance.off'
                                        defaultMessage='Off'
                                    />
                                </label>
                                <br />
                            </div>
                            <div className='mt-5'>
                                <FormattedMessage
                                    id='user.settings.advance.copyLoginWithAt.desc'
                                    defaultMessage='If enabled, you will be able to copy user login from profile and mini-profile with @ at the beginning'
                                />
                            </div>
                        </fieldset>,
                    ]}
                    setting={Preferences.ADVANCED_COPY_LOGIN_WITH_AT}
                    submit={this.handleSubmit}
                    saving={this.state.isSaving}
                    serverError={this.state.serverError}
                    updateSection={this.handleUpdateSection}
                />
            );
        }

        return (
            <SettingItemMin
                title={
                    <FormattedMessage
                        id='user.settings.advance.copyLoginWithAt.title'
                        defaultMessage="Copy user's login with @ from profile"
                    />
                }
                describe={this.renderOnOffLabel(this.state.settings.copy_login_with_at)}
                section={Preferences.ADVANCED_COPY_LOGIN_WITH_AT}
                updateSection={this.handleUpdateSection}
            />
        );
    }

    render() {
        const serverError = this.state.serverError || null;
        let ctrlSendSection;
        const {ctrlSendTitle, ctrlSendDesc} = this.getCtrlSendText();

        if (this.props.activeSection === 'advancedCtrlSend') {
            const ctrlSendActive = [
                this.state.settings.send_on_ctrl_enter === 'true',
                this.state.settings.send_on_ctrl_enter === 'false' &&
                    this.state.settings.code_block_ctrl_enter === 'true',
                this.state.settings.send_on_ctrl_enter === 'false' &&
                    this.state.settings.code_block_ctrl_enter === 'false',
            ];

            const inputs = [
                <fieldset key='ctrlSendSetting'>
                    <legend className='form-legend hidden-label'>
                        <FormattedMessage {...ctrlSendTitle} />
                    </legend>
                    <div className='radio'>
                        <label>
                            <input
                                id='ctrlSendOn'
                                type='radio'
                                name='sendOnCtrlEnter'
                                checked={ctrlSendActive[0]}
                                onChange={() =>
                                    this.updateSetting({
                                        send_on_ctrl_enter: 'true',
                                        code_block_ctrl_enter: 'true',
                                    })
                                }
                            />
                            <FormattedMessage
                                id='user.settings.advance.onForAllMessages'
                                defaultMessage='On for all messages'
                            />
                        </label>
                        <br />
                    </div>
                    <div className='radio'>
                        <label>
                            <input
                                id='ctrlSendOnForCode'
                                type='radio'
                                name='sendOnCtrlEnter'
                                checked={ctrlSendActive[1]}
                                onChange={() =>
                                    this.updateSetting({
                                        send_on_ctrl_enter: 'false',
                                        code_block_ctrl_enter: 'true',
                                    })
                                }
                            />
                            <FormattedMessage
                                id='user.settings.advance.onForCode'
                                defaultMessage='On only for code blocks starting with ```'
                            />
                        </label>
                        <br />
                    </div>
                    <div className='radio'>
                        <label>
                            <input
                                id='ctrlSendOff'
                                type='radio'
                                name='sendOnCtrlEnter'
                                checked={ctrlSendActive[2]}
                                onChange={() =>
                                    this.updateSetting({
                                        send_on_ctrl_enter: 'false',
                                        code_block_ctrl_enter: 'false',
                                    })
                                }
                            />
                            <FormattedMessage
                                id='user.settings.advance.off'
                                defaultMessage='Off'
                            />
                        </label>
                        <br />
                    </div>
                    <div>
                        <br />
                        <FormattedMessage {...ctrlSendDesc} />
                    </div>
                </fieldset>,
            ];
            ctrlSendSection = (
                <SettingItemMax
                    title={<FormattedMessage {...ctrlSendTitle} />}
                    inputs={inputs}
                    submit={this.handleSubmit.bind(this, ['send_on_ctrl_enter', 'code_block_ctrl_enter'])}
                    saving={this.state.isSaving}
                    serverError={serverError}
                    updateSection={this.handleUpdateSection}
                />
            );
        } else {
            ctrlSendSection = (
                <SettingItemMin
                    title={<FormattedMessage {...ctrlSendTitle} />}
                    describe={this.renderCtrlEnterLabel()}
                    section={'advancedCtrlSend'}
                    updateSection={this.handleUpdateSection}
                />
            );
        }

        const formattingSection = this.renderFormattingSection();
        let formattingSectionDivider = null;
        if (formattingSection) {
            formattingSectionDivider = <div className='divider-light' />;
        }

        const copyLoginWithAtSection = this.renderCopyLoginWithAtSection();
        let previewFeaturesSection;
        let previewFeaturesSectionDivider;
        if (this.state.previewFeaturesEnabled && PRE_RELEASE_FEATURES_KEYS.length > 0) {
            previewFeaturesSectionDivider = <div className='divider-light' />;

            if (this.props.activeSection === 'advancedPreviewFeatures') {
                const inputs = [];

                PRE_RELEASE_FEATURES_KEYS.forEach((key) => {
                    const feature = PRE_RELEASE_FEATURES[key];

                    inputs.push(
                        <div key={'advancedPreviewFeatures_' + feature.label}>
                            <div className='checkbox'>
                                <label>
                                    <input
                                        id={'advancedPreviewFeatures' + feature.label}
                                        type='checkbox'
                                        checked={
                                            this.state.settings[
                                                (Constants.FeatureTogglePrefix + feature.label) as PreReleaseFeatureFlag
                                            ] === 'true'
                                        }
                                        onChange={(e) => {
                                            this.toggleFeature(feature.label, e.target.checked);
                                        }}
                                    />
                                    <FormattedMessage
                                        id={getFeatureIntlId(key)}
                                        defaultMessage={getFeatureDescription(key)}
                                    />
                                </label>
                            </div>
                        </div>,
                    );
                });

                inputs.push(
                    <div key='advancedPreviewFeatures_helptext'>
                        <br />
                        <FormattedMessage
                            id='user.settings.advance.preReleaseDesc'
                            defaultMessage="Check any pre-released features you'd like to preview.  You may also need to refresh the page before the setting will take effect."
                        />
                    </div>,
                );
                previewFeaturesSection = (
                    <SettingItemMax
                        title={
                            <FormattedMessage
                                id='user.settings.advance.preReleaseTitle'
                                defaultMessage='Preview Pre-release Features'
                            />
                        }
                        inputs={inputs}
                        submit={this.saveEnabledFeatures}
                        saving={this.state.isSaving}
                        serverError={serverError}
                        updateSection={this.handleUpdateSection}
                    />
                );
            } else {
                previewFeaturesSection = (
                    <SettingItemMin
                        title={Utils.localizeMessage(
                            'user.settings.advance.preReleaseTitle',
                            'Preview Pre-release Features',
                        )}
                        describe={
                            <FormattedMessage
                                id='user.settings.advance.enabledFeatures'
                                defaultMessage='{count, number} {count, plural, one {feature} other {features}} enabled'
                                values={{count: this.state.enabledFeatures}}
                            />
                        }
                        section={'advancedPreviewFeatures'}
                        updateSection={this.handleUpdateSection}
                    />
                );
            }
        }

        let deactivateAccountSection: ReactNode = '';
        let makeConfirmationModal: ReactNode = '';
        const currentUser = this.props.currentUser;

        if (currentUser.auth_service === '' && this.props.enableUserDeactivation) {
            if (this.props.activeSection === 'deactivateAccount') {
                deactivateAccountSection = (
                    <SettingItemMax
                        title={
                            <FormattedMessage
                                id='user.settings.advance.deactivateAccountTitle'
                                defaultMessage='Deactivate Account'
                            />
                        }
                        inputs={[
                            <div key='formattingSetting'>
                                <div>
                                    <br />
                                    <FormattedMessage
                                        id='user.settings.advance.deactivateDesc'
                                        defaultMessage='Deactivating your account removes your ability to log in to this server and disables all email and mobile notifications. To reactivate your account, contact your System Administrator.'
                                    />
                                </div>
                            </div>,
                        ]}
                        saveButtonText={'Deactivate'}
                        setting={'deactivateAccount'}
                        submit={this.handleShowDeactivateAccountModal}
                        saving={this.state.isSaving}
                        serverError={this.state.serverError}
                        updateSection={this.handleUpdateSection}
                    />
                );
            } else {
                deactivateAccountSection = (
                    <SettingItemMin
                        title={
                            <FormattedMessage
                                id='user.settings.advance.deactivateAccountTitle'
                                defaultMessage='Deactivate Account'
                            />
                        }
                        describe={
                            <FormattedMessage
                                id='user.settings.advance.deactivateDescShort'
                                defaultMessage="Click 'Edit' to deactivate your account"
                            />
                        }
                        section={'deactivateAccount'}
                        updateSection={this.handleUpdateSection}
                    />
                );
            }

            const confirmButtonClass = 'btn btn-danger';
            const deactivateMemberButton = (
                <FormattedMessage
                    id='user.settings.advance.deactivate_member_modal.deactivateButton'
                    defaultMessage='Yes, deactivate my account'
                />
            );

            makeConfirmationModal = (
                <ConfirmModal
                    show={this.state.showDeactivateAccountModal}
                    title={
                        <FormattedMessage
                            id='user.settings.advance.confirmDeactivateAccountTitle'
                            defaultMessage='Confirm Deactivation'
                        />
                    }
                    message={
                        <FormattedMessage
                            id='user.settings.advance.confirmDeactivateDesc'
                            defaultMessage='Are you sure you want to deactivate your account? This can only be reversed by your System Administrator.'
                        />
                    }
                    confirmButtonClass={confirmButtonClass}
                    confirmButtonText={deactivateMemberButton}
                    onConfirm={this.handleDeactivateAccountSubmit}
                    onCancel={this.handleHideDeactivateAccountModal}
                />
            );
        }

        return (
            <div>
                <div className='modal-header'>
                    <button
                        id='closeButton'
                        type='button'
                        className='close'
                        data-dismiss='modal'
                        aria-label='Close'
                        onClick={this.props.closeModal}
                    >
                        <span aria-hidden='true'>{'×'}</span>
                    </button>
                    <h4 className='modal-title'>
                        <div className='modal-back'>
                            <span onClick={this.props.collapseModal}>
                                <BackIcon />
                            </span>
                        </div>
                        <FormattedMessage
                            id='user.settings.advance.title'
                            defaultMessage='Advanced Settings'
                        />
                    </h4>
                </div>
                <div className='user-settings'>
                    <h3 className='tab-header'>
                        <FormattedMessage
                            id='user.settings.advance.title'
                            defaultMessage='Advanced Settings'
                        />
                    </h3>
                    <div className='divider-dark first' />
                    {ctrlSendSection}
                    {formattingSectionDivider}
                    {formattingSection}
                    <div className='divider-light' />
                    <JoinLeaveSection
                        activeSection={this.props.activeSection}
                        onUpdateSection={this.handleUpdateSection}
                        renderOnOffLabel={this.renderOnOffLabel}
                    />
                    <div className='divider-light' />
                    {copyLoginWithAtSection}
                    {previewFeaturesSectionDivider}
                    {previewFeaturesSection}
                    {formattingSectionDivider}
                    <PerformanceDebuggingSection
                        activeSection={this.props.activeSection}
                        onUpdateSection={this.handleUpdateSection}
                    />
                    {deactivateAccountSection}
                    <div className='divider-dark' />
                    {makeConfirmationModal}
                </div>
            </div>
        );
    }
}
